import React, { useCallback } from 'react';
import { Modal, useWindowDimensions } from 'react-native';
import {
  VStack, HStack, Heading, Pressable, Icon, useMedia, Box,
} from '@gluestack-ui/themed';
import { X } from 'lucide-react-native';
import { SafeAreaView } from 'common/components/SafeAreaView';
import { VideoPlayer } from '../index';
import { VideoPlayerProps } from '../core/types';
import { usePersistVideoProgress } from '../hooks/usePersistVideoProgress';

interface VideoModalProps extends VideoPlayerProps {
  isVisible: boolean;
  onClose: () => void;
}

export const VideoModal: React.FC<VideoModalProps> = ({
  isVisible,
  onClose,
  ...videoProps
}) => {
  const { persistProgress } = usePersistVideoProgress({ resourceId: videoProps?.resource?.id });
  const { height } = useWindowDimensions();
  const media = useMedia();
  const isMobile = !media.md;

  // Create a proper close handler that will be passed to VideoPlayer
  // This ensures persistence happens before modal closes
  const handleClose = useCallback(() => {
    onClose();
    persistProgress();
  }, [onClose, persistProgress]);

  // YouTube-like mobile experience: dark overlay, minimal chrome
  // Desktop: white modal with padding and title
  const modalBg = isMobile ? '$black' : '$white';
  const modalWidth = isMobile ? '100%' : (media.lg ? 900 : 600);
  const modalHeight = isMobile ? height : height - 48;
  const modalMargin = isMobile ? 0 : 24;
  const modalRadius = isMobile ? 0 : 12;

  return (
    <Modal
      animationType="slide"
      transparent
      visible={isVisible}
      onRequestClose={handleClose}
    >
      <Box
        flex={1}
        bg={isMobile ? '$black' : 'rgba(0, 0, 0, 0.5)'}
        justifyContent="center"
        alignItems="center"
      >
        <SafeAreaView
          w={modalWidth}
          h={modalHeight}
          maxHeight={modalHeight}
          alignSelf="center"
          my={modalMargin}
          bg={modalBg}
          borderRadius={modalRadius}
          overflow="hidden"
        >
          <VStack flex={1} position="relative">
            {/* Desktop: White header with title and close button */}
            {!isMobile && (
              <HStack height={60} alignItems="center" justifyContent="space-between" px={24} borderBottomWidth={1} borderBottomColor="$borderLight200">
                <Heading size="sm" numberOfLines={1} flex={1} pr="$4">{videoProps?.resource?.title}</Heading>
                <Pressable testID="button-close-video-modal" onPress={handleClose} hitSlop={8}>
                  <Icon as={X} size="xl" color="$textHeading" />
                </Pressable>
              </HStack>
            )}

            {/* Mobile: Floating close button over video (with higher z-index) */}
            {isMobile && (
              <Pressable
                position="absolute"
                top={8}
                right={8}
                zIndex={1000}
                onPress={handleClose}
                p="$2.5"
                bg="rgba(0,0,0,0.7)"
                borderRadius="$full"
                hitSlop={8}
              >
                <Icon as={X} color="$white" size="lg" />
              </Pressable>
            )}

            {/* Video player and transcript - full width on desktop for immersive experience */}
            <Box flex={1}>
              <VideoPlayer {...videoProps} onClose={handleClose} />
            </Box>
          </VStack>
        </SafeAreaView>
      </Box>
    </Modal>
  );
};
